import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useTutorial } from "../../contexts/TutorialContext";

const PADDING = 8;
const TOOLTIP_W = 320;
const TOOLTIP_GAP = 14;

function getTargetRect(targetKey) {
  if (!targetKey) return null;
  const el = document.querySelector(`[data-tutorial="${targetKey}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
  };
}

function tooltipPos(rect, placement) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect || placement === "center") {
    return {
      top: Math.max(16, vh / 2 - 110),
      left: Math.max(16, vw / 2 - TOOLTIP_W / 2),
    };
  }
  let top = rect.top;
  let left = rect.left;
  if (placement === "right") {
    left = rect.left + rect.width + TOOLTIP_GAP;
    top = rect.top;
  } else if (placement === "bottom") {
    top = rect.top + rect.height + TOOLTIP_GAP;
    left = rect.left;
  } else if (placement === "left") {
    left = rect.left - TOOLTIP_W - TOOLTIP_GAP;
    top = rect.top;
  } else if (placement === "top") {
    top = rect.top - TOOLTIP_GAP - 160;
    left = rect.left;
  }
  // Clamp inside viewport.
  left = Math.min(Math.max(16, left), vw - TOOLTIP_W - 16);
  top = Math.min(Math.max(16, top), vh - 200);
  return { top, left };
}

export default function TutorialOverlay() {
  const { active, step, stepIndex, totalSteps, next, back, skip } = useTutorial();
  const [rect, setRect] = useState(null);
  const [tick, setTick] = useState(0);

  // Recompute target rect when step changes / on resize / scroll.
  useLayoutEffect(() => {
    if (!active || !step) return undefined;
    let raf;
    const measure = () => {
      setRect(getTargetRect(step.target));
    };
    // Wait a frame to let layout settle (e.g., navigation).
    raf = requestAnimationFrame(measure);
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [active, step, tick]);

  // If the target hasn't mounted yet, retry briefly.
  useEffect(() => {
    if (!active || !step?.target) return undefined;
    if (rect) return undefined;
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      setTick((t) => t + 1);
      if (attempts > 20) clearInterval(id);
    }, 100);
    return () => clearInterval(id);
  }, [active, step, rect]);

  // Esc closes the tutorial.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") skip();
      else if (e.key === "ArrowRight" || e.key === "Enter") next();
      else if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, next, back, skip]);

  if (!active || !step) return null;

  const hasTarget = !!step.target && !!rect;
  const placement = step.placement || "right";
  const pos = tooltipPos(rect, hasTarget ? placement : "center");
  const isLast = stepIndex === totalSteps - 1;

  const overlay = (
    <div className="fixed inset-0 z-[9998] pointer-events-none">
      {/* Dim layer with cut-out via SVG mask */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-auto"
        onClick={(e) => {
          // Click on dim layer to advance.
          if (e.target === e.currentTarget) next();
        }}
        aria-hidden
      >
        <defs>
          <mask id="tutorial-mask">
            <rect width="100%" height="100%" fill="white" />
            {hasTarget && (
              <rect
                x={rect.left - PADDING}
                y={rect.top - PADDING}
                width={rect.width + PADDING * 2}
                height={rect.height + PADDING * 2}
                rx="8"
                ry="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(11, 16, 56, 0.55)"
          mask="url(#tutorial-mask)"
        />
      </svg>

      {/* Highlight ring around the target */}
      {hasTarget && (
        <motion.div
          key={step.key}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.18 }}
          className="absolute rounded-md pointer-events-none"
          style={{
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2,
            boxShadow:
              "0 0 0 2px #2563eb, 0 0 0 6px rgba(37,99,235,0.25), 0 10px 30px rgba(11,16,56,0.25)",
          }}
        />
      )}

      {/* Tooltip card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step.key}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.16 }}
          className="absolute bg-white border border-gray-200 rounded-md shadow-xl p-4 pointer-events-auto font-sans text-[#0B1038]"
          style={{ top: pos.top, left: pos.left, width: TOOLTIP_W }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
              Step {stepIndex + 1} of {totalSteps}
            </div>
            <button
              type="button"
              onClick={skip}
              className="text-gray-400 hover:text-[#0B1038] -mt-1 -mr-1 p-1 rounded"
              aria-label="Close tutorial"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <h3 className="font-display text-xl tracking-tight mt-1">
            {step.title}
          </h3>
          <p className="text-sm text-gray-600 mt-2 leading-relaxed">
            {step.body}
          </p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mt-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === stepIndex
                    ? "bg-blue-600 w-5"
                    : i < stepIndex
                    ? "bg-blue-300 w-1.5"
                    : "bg-gray-200 w-1.5"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between mt-4">
            <button
              type="button"
              onClick={skip}
              className="text-xs text-gray-500 hover:text-[#0B1038] font-medium"
            >
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              {stepIndex > 0 && (
                <button
                  type="button"
                  onClick={back}
                  className="px-3 py-1.5 text-sm rounded-sm border border-gray-300 font-semibold hover:bg-gray-50"
                >
                  Back
                </button>
              )}
              <button
                type="button"
                onClick={next}
                className="px-3 py-1.5 text-sm rounded-sm bg-[#0B1038] text-white font-semibold hover:bg-[#151c54]"
              >
                {isLast ? "Got it" : "Next"}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );

  return createPortal(overlay, document.body);
}
